import { useState } from "react";
import { Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useCreateShareLink } from "@/resources/share";
import type { ShareableType, CreateShareParams } from "@/types";

const FROZEN_TYPES: ShareableType[] = ["seo_insights", "rank_tracker_ai_report", "rank_tracker_page"];

interface ShareLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareableType: ShareableType;
  shareableId?: string;
  frozenPayload?: Record<string, unknown>;
  resourceName: string;
}

export function ShareLinkModal({
  open,
  onOpenChange,
  shareableType,
  shareableId,
  frozenPayload,
  resourceName,
}: ShareLinkModalProps) {
  const [expiryDays, setExpiryDays] = useState<string>("30");
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isFrozen = FROZEN_TYPES.includes(shareableType);
  const { mutate: createLink, isPending: isCreating } = useCreateShareLink();

  const handleGenerate = () => {
    const days = expiryDays === "never" ? null : Number(expiryDays);

    let params: CreateShareParams;
    if (isFrozen) {
      params = {
        shareable_type: shareableType as "seo_insights" | "rank_tracker_ai_report" | "rank_tracker_page",
        frozen_payload: frozenPayload ?? {},
        expires_in_days: days,
      };
    } else {
      params = {
        shareable_type: shareableType as "scorecard" | "landscape" | "search_term_set",
        shareable_id: shareableId ?? "",
        expires_in_days: days,
      };
    }

    createLink(params, {
      onSuccess: (data) => {
        setGeneratedUrl(data.url);
        toast.success("Share link created");
      },
    });
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setGeneratedUrl(null);
      setCopied(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share {resourceName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label>Link Expiry</Label>
            <Select value={expiryDays} onValueChange={setExpiryDays}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            className="w-full"
            onClick={handleGenerate}
            disabled={isCreating}
          >
            {isCreating ? "Generating..." : "Generate Link"}
          </Button>

          {generatedUrl && (
            <div className="space-y-2">
              <Label>Share URL</Label>
              <div className="flex gap-2">
                <Input
                  value={generatedUrl}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(generatedUrl)}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
